import React, { useState, useCallback, useEffect } from 'react';
import { apiService } from '../services/apiService';
import {
  Inbox, RefreshCw, Plus, X, Mail, Send, Search, Trash2, Sparkles,
  ArrowDownLeft, ArrowUpRight, CheckCircle2, Settings2, Loader2, AtSign,
} from 'lucide-react';

const C = {
  bg: 'var(--c-bg)', bg1: 'var(--c-bg-1)', bg2: 'var(--c-bg-2)',
  surface: 'var(--c-surface)', border: 'var(--c-border)',
  primary: 'var(--c-primary)', text: 'var(--c-text)', text2: 'var(--c-text-2)', text3: 'var(--c-text-3)',
};

const INTENT_META = {
  hot_lead: { label: 'Hot lead', color: '#EF4444', bg: 'rgba(239,68,68,0.14)' },
  meeting_request: { label: 'Meeting', color: '#10B981', bg: 'rgba(16,185,129,0.14)' },
  needs_info: { label: 'Wants info', color: '#F59E0B', bg: 'rgba(245,158,11,0.14)' },
  follow_up_later: { label: 'Follow up', color: '#3B82F6', bg: 'rgba(59,130,246,0.14)' },
  out_of_office: { label: 'OOO', color: '#8B5CF6', bg: 'rgba(139,92,246,0.14)' },
  not_interested: { label: 'Not interested', color: '#6B7280', bg: 'rgba(107,114,128,0.14)' },
  other: { label: 'Other', color: '#6B7280', bg: 'rgba(107,114,128,0.14)' },
};

const PROVIDERS = [
  { key: 'gmail', label: 'Gmail / Google Workspace', hint: 'Use an App Password (Google Account → Security → 2-Step Verification → App passwords)' },
  { key: 'zoho', label: 'Zoho Mail', hint: 'Use an App Password (Zoho Account → Security → App Passwords)' },
  { key: 'outlook', label: 'Outlook / Microsoft 365', hint: 'Use an App Password from your Microsoft account security settings' },
  { key: 'custom', label: 'Other (custom IMAP/SMTP)', hint: 'Enter your provider’s IMAP and SMTP hosts below' },
];

const fmtWhen = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const now = new Date();
  const sameDay = dt.toDateString() === now.toDateString();
  if (sameDay) return dt.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
  return dt.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
};

const InboxManager = () => {
  const [messages, setMessages] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [accountFilter, setAccountFilter] = useState(null);
  const [directionFilter, setDirectionFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [showConnect, setShowConnect] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [toasts, setToasts] = useState([]);

  // connect-mailbox form
  const [form, setForm] = useState({ provider: 'gmail', email: '', password: '', display_name: '', capture_mode: 'all', imap_host: '', smtp_host: '', imap_port: '', smtp_port: '' });
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectError, setConnectError] = useState('');

  const showToast = useCallback((type, msg) => {
    const id = Date.now();
    setToasts(p => [...p, { id, type, msg }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 5000);
  }, []);

  const fetchMessages = useCallback(async () => {
    setIsLoading(true);
    try {
      const r = await apiService.getInboxMessages({
        accountId: accountFilter, direction: directionFilter, q: search, limit: 150,
      });
      setMessages(r.messages || []);
      if (r.accounts) setAccounts(prev => {
        // keep richer account objects if we already have them
        return prev.length && prev[0].capture_mode !== undefined ? prev : r.accounts;
      });
    } catch { showToast('error', 'Failed to load inbox'); }
    finally { setIsLoading(false); }
  }, [accountFilter, directionFilter, search, showToast]);

  const fetchAccounts = useCallback(async () => {
    try {
      const r = await apiService.getEmailAccounts();
      if (r.accounts) setAccounts(r.accounts);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);
  useEffect(() => { fetchMessages(); }, [fetchMessages]);
  useEffect(() => {
    const t = setInterval(fetchMessages, 60000); // live refresh every 60s
    return () => clearInterval(t);
  }, [fetchMessages]);

  const syncNow = useCallback(async () => {
    setIsSyncing(true);
    try {
      const r = await apiService.syncMailboxes();
      if (r.status === 'success') {
        showToast('success', `Synced ${r.accounts} mailbox${r.accounts === 1 ? '' : 'es'} — ${r.inbound} new received, ${r.outbound} sent captured`);
        fetchMessages();
      } else showToast('error', r.error || 'Sync failed');
    } catch { showToast('error', 'Sync failed'); }
    finally { setIsSyncing(false); }
  }, [fetchMessages, showToast]);

  const connectMailbox = useCallback(async () => {
    setConnectError('');
    if (!form.email || !form.password) { setConnectError('Email and app password are required.'); return; }
    setIsConnecting(true);
    try {
      const payload = { ...form };
      if (form.provider !== 'custom') { delete payload.imap_host; delete payload.smtp_host; delete payload.imap_port; delete payload.smtp_port; }
      const r = await apiService.addEmailAccount(payload);
      if (r.status === 'success') {
        showToast('success', `✓ ${form.email} connected & verified`);
        setShowConnect(false);
        setForm({ provider: 'gmail', email: '', password: '', display_name: '', capture_mode: 'all', imap_host: '', smtp_host: '', imap_port: '', smtp_port: '' });
        fetchAccounts();
        syncNow();
      } else setConnectError(r.error || 'Could not connect this mailbox.');
    } catch (e) { setConnectError('Could not connect — check the address and app password.'); }
    finally { setIsConnecting(false); }
  }, [form, fetchAccounts, showToast, syncNow]);

  const toggleAccount = useCallback(async (acct, field, value) => {
    try {
      await apiService.updateEmailAccount(acct.id, { [field]: value });
      fetchAccounts();
      showToast('success', 'Account updated');
    } catch { showToast('error', 'Update failed'); }
  }, [fetchAccounts, showToast]);

  const removeAccount = useCallback(async (acct) => {
    if (!window.confirm(`Remove ${acct.email}? Its captured messages stay in the system.`)) return;
    try {
      await apiService.deleteEmailAccount(acct.id);
      fetchAccounts(); fetchMessages();
      showToast('success', `${acct.email} removed`);
    } catch { showToast('error', 'Failed to remove'); }
  }, [fetchAccounts, fetchMessages, showToast]);

  const openMessage = useCallback(async (m) => {
    setSelectedMsg({ ...m, _loading: true });
    try {
      const r = await apiService.getReplyDetails(m.id);
      const full = r.reply || r.data || m;
      setSelectedMsg({ ...m, ...full, _loading: false });
      if (!m.is_read && m.direction === 'inbound') {
        apiService.markReplyRead(m.id).then(() => {
          setMessages(prev => prev.map(x => x.id === m.id ? { ...x, is_read: true } : x));
        }).catch(() => {});
      }
    } catch { setSelectedMsg(prev => prev ? { ...prev, _loading: false } : null); }
  }, []);

  // ── styles ──────────────────────────────────────────────────────────────
  const s = {
    page: { minHeight: 'calc(100vh - 64px)', background: C.bg, padding: '28px 24px' },
    inner: { maxWidth: 1200, margin: '0 auto' },
    chip: (active, color = C.primary) => ({
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 99,
      fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter,sans-serif',
      border: `1px solid ${active ? color : C.border}`,
      background: active ? 'rgba(0,179,134,0.10)' : C.surface,
      color: active ? color : C.text2, transition: 'all .15s',
    }),
    row: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
    msgRow: (unread) => ({
      display: 'flex', alignItems: 'center', gap: 14, padding: '13px 16px',
      background: unread ? 'rgba(0,179,134,0.045)' : C.bg1,
      border: `1px solid ${C.border}`, borderRadius: 12, cursor: 'pointer',
      transition: 'transform .12s, border-color .12s', marginBottom: 8,
    }),
    badge: (color, bg) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 9px', borderRadius: 99, fontSize: 10.5, fontWeight: 700, color, background: bg, whiteSpace: 'nowrap' }),
    btn: { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '10px 18px', background: 'linear-gradient(135deg, var(--c-primary), var(--c-primary-dim))', color: 'var(--c-btn-text)', fontWeight: 700, fontSize: 13, border: 'none', borderRadius: 10, cursor: 'pointer', fontFamily: 'Inter,sans-serif' },
    btn2: { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '10px 18px', background: C.surface, color: C.text, fontWeight: 600, fontSize: 13, border: `1px solid ${C.border}`, borderRadius: 10, cursor: 'pointer', fontFamily: 'Inter,sans-serif' },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(5,11,20,0.75)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 },
    modal: { background: C.bg1, border: '1px solid var(--c-border-bright)', borderRadius: 20, maxWidth: 560, width: '100%', maxHeight: '88vh', overflowY: 'auto', boxShadow: 'var(--shadow-elevated)' },
    input: { width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, padding: '11px 14px', fontSize: 13.5, fontFamily: 'Inter,sans-serif', outline: 'none', boxSizing: 'border-box' },
    label: { fontSize: 11.5, fontWeight: 600, color: C.text3, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6, display: 'block' },
    drawer: { position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(680px, 100vw)', zIndex: 110, background: C.bg1, borderLeft: '1px solid var(--c-border-bright)', display: 'flex', flexDirection: 'column', boxShadow: '-24px 0 60px rgba(0,0,0,0.45)' },
  };

  const acctColor = (id) => {
    const palette = ['#00B386', '#3B82F6', '#F59E0B', '#8B5CF6', '#EF4444', '#10B981'];
    return palette[(id || 0) % palette.length];
  };

  return (
    <div style={s.page}>
      <style>{`.im-row:hover { transform: translateY(-1px); border-color: var(--c-border-bright) !important; }`}</style>
      <div style={s.inner}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
              <Inbox size={22} color={C.primary} />
              <h1 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 24, fontWeight: 700, color: C.text, margin: 0 }}>Unified Inbox</h1>
            </div>
            <p style={{ color: C.text2, fontSize: 13, margin: 0 }}>
              Every message across {accounts.length} connected mailbox{accounts.length === 1 ? '' : 'es'} — refreshed live every minute
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setShowManage(true)} style={s.btn2}><Settings2 size={14} /> Mailboxes</button>
            <button onClick={syncNow} disabled={isSyncing} style={s.btn2}>
              {isSyncing ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={14} />}
              {isSyncing ? 'Syncing…' : 'Sync now'}
            </button>
            <button onClick={() => setShowConnect(true)} style={s.btn}><Plus size={15} /> Connect mailbox</button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ ...s.row, marginBottom: 16 }}>
          <span onClick={() => setAccountFilter(null)} style={s.chip(!accountFilter)}>All accounts</span>
          {accounts.map(a => (
            <span key={a.id} onClick={() => setAccountFilter(a.id === accountFilter ? null : a.id)} style={s.chip(accountFilter === a.id, acctColor(a.id))}>
              <span style={{ width: 7, height: 7, borderRadius: 99, background: acctColor(a.id) }} />
              {a.email}
            </span>
          ))}
          <span style={{ width: 1, height: 22, background: C.border, margin: '0 4px' }} />
          <span onClick={() => setDirectionFilter(null)} style={s.chip(!directionFilter)}>All</span>
          <span onClick={() => setDirectionFilter(directionFilter === 'inbound' ? null : 'inbound')} style={s.chip(directionFilter === 'inbound')}><ArrowDownLeft size={13} /> Received</span>
          <span onClick={() => setDirectionFilter(directionFilter === 'outbound' ? null : 'outbound')} style={s.chip(directionFilter === 'outbound')}><ArrowUpRight size={13} /> Sent</span>
          <div style={{ position: 'relative', marginLeft: 'auto', minWidth: 220 }}>
            <Search size={14} color={C.text3} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search sender, subject…" style={{ ...s.input, paddingLeft: 34 }} />
          </div>
        </div>

        {/* Message list */}
        {isLoading && !messages.length ? (
          <div style={{ textAlign: 'center', padding: 60, color: C.text3, fontSize: 13 }}>Loading messages…</div>
        ) : !messages.length ? (
          <div style={{ textAlign: 'center', padding: '70px 24px', background: C.surface, border: `1px dashed ${C.border}`, borderRadius: 16 }}>
            <Mail size={36} color={C.text3} style={{ marginBottom: 12 }} />
            <p style={{ color: C.text2, fontSize: 14.5, fontWeight: 600, margin: '0 0 4px' }}>No messages yet</p>
            <p style={{ color: C.text3, fontSize: 12.5, margin: 0 }}>Connect a mailbox or hit Sync now — new mail lands here within a minute.</p>
          </div>
        ) : (
          messages.map(m => {
            const isOut = m.direction === 'outbound';
            const im = INTENT_META[m.intent];
            return (
              <div key={m.id} className="im-row" onClick={() => openMessage(m)} style={s.msgRow(!m.is_read && !isOut)}>
                <div style={{ width: 34, height: 34, borderRadius: 10, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isOut ? 'rgba(0,179,134,0.12)' : C.surface, border: `1px solid ${C.border}` }}>
                  {isOut ? <ArrowUpRight size={15} color={C.primary} /> : <ArrowDownLeft size={15} color={!m.is_read ? C.primary : C.text3} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: !m.is_read && !isOut ? 700 : 600, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {isOut ? `To: ${m.to_email}` : (m.from_name || m.from_email)}
                    </span>
                    {im && <span style={s.badge(im.color, im.bg)}>{im.label}</span>}
                    {typeof m.lead_score === 'number' && m.lead_score >= 60 && <span style={s.badge('#EF4444', 'rgba(239,68,68,0.12)')}>🔥 {m.lead_score}</span>}
                    {m.is_replied && !isOut && <span title="Answered"><CheckCircle2 size={13} color={C.primary} /></span>}
                  </div>
                  <div style={{ fontSize: 12.5, color: C.text2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.subject || '(no subject)'}</div>
                  <div style={{ fontSize: 11.5, color: C.text3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 1 }}>{(m.body || '').replace(/\s+/g, ' ').slice(0, 130)}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 11.5, color: C.text3, marginBottom: 4 }}>{fmtWhen(m.received_at)}</div>
                  {m.account_email && (
                    <span style={{ ...s.badge(acctColor(m.account_id), 'transparent'), border: `1px solid ${C.border}`, fontSize: 10 }}>
                      <AtSign size={9} /> {m.account_email.split('@')[0]}
                    </span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ── Message detail drawer ── */}
      {selectedMsg && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(5,11,20,0.6)', zIndex: 105 }} onClick={() => setSelectedMsg(null)} />
          <div style={s.drawer}>
            <div style={{ padding: '18px 22px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15.5, fontWeight: 700, color: C.text, marginBottom: 3 }}>{selectedMsg.subject || '(no subject)'}</div>
                <div style={{ fontSize: 12.5, color: C.text2 }}>
                  {selectedMsg.direction === 'outbound'
                    ? <>You → {selectedMsg.to_email}</>
                    : <>{selectedMsg.from_name || selectedMsg.from_email} → {selectedMsg.account_email || 'you'}</>}
                  {' · '}{new Date(selectedMsg.received_at).toLocaleString('en-ZA')}
                </div>
              </div>
              <button onClick={() => setSelectedMsg(null)} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, width: 32, height: 32, cursor: 'pointer', color: C.text2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={15} /></button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px' }}>
              {selectedMsg.ai_summary && selectedMsg.direction !== 'outbound' && (
                <div style={{ marginBottom: 16, padding: '12px 14px', borderRadius: 12, background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.18)' }}>
                  <span style={{ fontSize: 10.5, fontWeight: 700, color: '#8B5CF6', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Sparkles size={11} /> AI SUMMARY</span>
                  <p style={{ fontSize: 12.5, color: C.text2, margin: '5px 0 0', lineHeight: 1.6 }}>{String(selectedMsg.ai_summary).slice(0, 400)}</p>
                </div>
              )}
              <div style={{ fontSize: 13.5, color: C.text, whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                {selectedMsg._loading ? 'Loading…' : (selectedMsg.body || '(empty message)')}
              </div>
            </div>
            {selectedMsg.lead_id && (
              <div style={{ padding: '14px 22px', borderTop: `1px solid ${C.border}`, fontSize: 12.5, color: C.text2 }}>
                💼 This message is linked to a pipeline lead — open the <strong style={{ color: C.text }}>Sales Pipeline</strong> to see the full conversation and reply.
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Connect mailbox modal ── */}
      {showConnect && (
        <div style={s.overlay} onClick={e => e.target === e.currentTarget && setShowConnect(false)}>
          <div style={s.modal}>
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 18, fontWeight: 700, color: C.text, margin: 0 }}>Connect a mailbox</h2>
              <button onClick={() => setShowConnect(false)} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, width: 30, height: 30, cursor: 'pointer', color: C.text2 }}><X size={14} /></button>
            </div>
            <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={s.label}>Provider</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {PROVIDERS.map(p => (
                    <span key={p.key} onClick={() => setForm(f => ({ ...f, provider: p.key }))} style={s.chip(form.provider === p.key)}>{p.label}</span>
                  ))}
                </div>
                <p style={{ fontSize: 11.5, color: C.text3, margin: '8px 0 0' }}>{PROVIDERS.find(p => p.key === form.provider)?.hint}</p>
              </div>
              <div>
                <label style={s.label}>Email address</label>
                <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="info@writenowagency.co.za" style={s.input} />
              </div>
              <div>
                <label style={s.label}>App password</label>
                <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="•••• •••• •••• ••••" style={s.input} />
              </div>
              <div>
                <label style={s.label}>Display name (used as sender name)</label>
                <input value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))} placeholder="WriteNow Agency" style={s.input} />
              </div>
              {form.provider === 'custom' && (
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
                  <input value={form.imap_host} onChange={e => setForm(f => ({ ...f, imap_host: e.target.value }))} placeholder="imap.example.com" style={s.input} />
                  <input value={form.imap_port} onChange={e => setForm(f => ({ ...f, imap_port: e.target.value }))} placeholder="993" style={s.input} />
                  <input value={form.smtp_host} onChange={e => setForm(f => ({ ...f, smtp_host: e.target.value }))} placeholder="smtp.example.com" style={s.input} />
                  <input value={form.smtp_port} onChange={e => setForm(f => ({ ...f, smtp_port: e.target.value }))} placeholder="587" style={s.input} />
                </div>
              )}
              <div>
                <label style={s.label}>What should we capture?</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <span onClick={() => setForm(f => ({ ...f, capture_mode: 'all' }))} style={s.chip(form.capture_mode === 'all')}>📥 Everything (full inbox)</span>
                  <span onClick={() => setForm(f => ({ ...f, capture_mode: 'matched_only' }))} style={s.chip(form.capture_mode === 'matched_only')}>🎯 Campaign replies only</span>
                </div>
              </div>
              {connectError && (
                <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#EF4444', fontSize: 12.5 }}>{connectError}</div>
              )}
              <button onClick={connectMailbox} disabled={isConnecting} style={{ ...s.btn, justifyContent: 'center', opacity: isConnecting ? 0.6 : 1 }}>
                {isConnecting ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Verifying credentials…</> : <><CheckCircle2 size={15} /> Connect & verify</>}
              </button>
              <p style={{ fontSize: 11, color: C.text3, margin: 0, textAlign: 'center' }}>We test the IMAP + SMTP login before saving. Messages start syncing within a minute.</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Manage mailboxes modal ── */}
      {showManage && (
        <div style={s.overlay} onClick={e => e.target === e.currentTarget && setShowManage(false)}>
          <div style={s.modal}>
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 18, fontWeight: 700, color: C.text, margin: 0 }}>Connected mailboxes</h2>
              <button onClick={() => setShowManage(false)} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, width: 30, height: 30, cursor: 'pointer', color: C.text2 }}><X size={14} /></button>
            </div>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {!accounts.length && <p style={{ color: C.text3, fontSize: 13, textAlign: 'center', padding: 20 }}>No mailboxes connected yet.</p>}
              {accounts.map(a => (
                <div key={a.id} style={{ border: `1px solid ${C.border}`, borderRadius: 14, padding: 16, background: C.surface }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <span style={{ width: 9, height: 9, borderRadius: 99, background: a.is_active ? '#10B981' : '#6B7280' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>{a.email} {a.is_default && <span style={{ ...s.badge(C.primary, 'rgba(0,179,134,0.12)'), marginLeft: 6 }}>default sender</span>}</div>
                      <div style={{ fontSize: 11.5, color: C.text3 }}>
                        {a.provider} · {a.capture_mode === 'matched_only' ? 'campaign replies only' : 'captures everything'}
                        {a.last_synced_at && ` · last sync ${fmtWhen(a.last_synced_at)}`}
                      </div>
                      {a.last_error && <div style={{ fontSize: 11.5, color: '#EF4444', marginTop: 3 }}>⚠ {String(a.last_error).slice(0, 90)}</div>}
                    </div>
                    <button onClick={() => removeAccount(a)} title="Remove" style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 8, width: 30, height: 30, cursor: 'pointer', color: '#EF4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Trash2 size={13} /></button>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span onClick={() => toggleAccount(a, 'is_active', !a.is_active)} style={s.chip(a.is_active)}>{a.is_active ? 'Active' : 'Paused'}</span>
                    <span onClick={() => toggleAccount(a, 'capture_mode', a.capture_mode === 'all' ? 'matched_only' : 'all')} style={s.chip(a.capture_mode === 'all')}>
                      {a.capture_mode === 'all' ? '📥 Everything' : '🎯 Replies only'}
                    </span>
                    {!a.is_default && <span onClick={() => toggleAccount(a, 'is_default', true)} style={s.chip(false)}>Make default sender</span>}
                  </div>
                </div>
              ))}
              <button onClick={() => { setShowManage(false); setShowConnect(true); }} style={{ ...s.btn2, justifyContent: 'center' }}><Plus size={14} /> Connect another mailbox</button>
            </div>
          </div>
        </div>
      )}

      {/* Toasts */}
      <div style={{ position: 'fixed', bottom: 24, right: 24, display: 'flex', flexDirection: 'column', gap: 10, zIndex: 200 }}>
        {toasts.map(t => (
          <div key={t.id} style={{ padding: '12px 18px', borderRadius: 12, fontSize: 13, fontWeight: 500, backdropFilter: 'blur(16px)', boxShadow: 'var(--shadow-elevated)', background: t.type === 'error' ? 'rgba(239,68,68,0.08)' : 'rgba(0,179,134,0.08)', border: `1px solid ${t.type === 'error' ? 'rgba(239,68,68,0.15)' : 'rgba(0,179,134,0.15)'}`, color: t.type === 'error' ? 'var(--c-red)' : 'var(--c-primary)' }}>
            {t.msg}
          </div>
        ))}
      </div>
    </div>
  );
};

export default InboxManager;
